'use client';

import { type PanelSize, usePanelRef } from 'react-resizable-panels';

import { useCallback, useEffect, useRef, useState } from 'react';

import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';

import { cn } from '@/utils/styles';

/** Config for the optional right panel of ResizableLayout. */
export type ResizableLayoutRightPanel = {
	node: React.ReactNode;
	open: boolean;
	/** Size to open at, read at open time (e.g. a persisted pixel or percentage preference). */
	getOpenSize: () => string;
	/** Changing this identity resizes an already-open panel to getOpenSize(). */
	openSizeRequestKey?: string | number;
	minSize: string;
	maxSize?: string;
	mainMinSize?: string;
	groupResizeBehavior?: 'preserve-relative-size' | 'preserve-pixel-size';
	handleClassName?: string;
	/** Keeps animated content from rewrapping; omit for naturally fluid panel content. */
	contentMinWidthPx?: number;
	/** User dragged the handle to a new size (not fired for programmatic/animated changes). */
	onUserResize?: (size: PanelSize) => void;
};

type ResizableLayoutProps = {
	children: React.ReactNode;
	/** Omit for a plain full-width layout; pass to get a resizable right split. */
	rightPanel?: ResizableLayoutRightPanel;
};

/** The app's standard content layout: main content plus an optional resizable right panel that animates open/close via collapse/expand instead of mount/unmount. */
export function ResizableLayout({ children, rightPanel }: ResizableLayoutProps) {
	const hasPanel = !!rightPanel;
	const open = !!rightPanel?.open;
	const getOpenSize = rightPanel?.getOpenSize;
	const openSizeRequestKey = rightPanel?.openSizeRequestKey;
	const panelRef = usePanelRef();
	const [isDragging, setIsDragging] = useState(false);

	// Panel content mounts on first open, then stays mounted so its state survives close/reopen.
	const [hasOpened, setHasOpened] = useState(false);
	if (open && !hasOpened) setHasOpened(true);

	// Animate only real open-value flips — never the mount/remount settling (Strict Mode double-runs effects, so key on value change, not run count).
	const prevOpenRef = useRef(open);
	const [animate, setAnimate] = useState(false);

	// Retry across frames until the panel reaches the target state — survives the remount registration throw AND a silent collapse() no-op before the collapsible=true constraint commits.
	useEffect(() => {
		if (!hasPanel) return;
		if (prevOpenRef.current !== open) setAnimate(true);
		prevOpenRef.current = open;
		let frame = 0;
		let attempts = 0;
		const apply = () => {
			const panel = panelRef.current;
			try {
				if (open) {
					panel?.resize(getOpenSize?.() ?? '0px');
					if (panel && panel.isCollapsed() && attempts++ < 10) frame = requestAnimationFrame(apply);
				} else {
					panel?.collapse();
					if (panel && !panel.isCollapsed() && attempts++ < 10)
						frame = requestAnimationFrame(apply);
				}
			} catch {
				if (attempts++ < 10) frame = requestAnimationFrame(apply);
			}
		};
		apply();
		return () => cancelAnimationFrame(frame);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, hasPanel, openSizeRequestKey, panelRef]);

	// Suspend the transition while dragging so the handle tracks the pointer 1:1.
	const handleDragStart = useCallback(() => {
		setIsDragging(true);
		window.addEventListener('pointerup', () => setIsDragging(false), { once: true });
	}, []);

	return (
		// The library owns the panels' inline flex-grow, so the animation is a child-selector transition on the panel roots.
		<ResizablePanelGroup
			className={cn(
				'flex-1 overflow-hidden',
				animate &&
					!isDragging &&
					'[&>[data-panel]]:ease-[cubic-bezier(0.32,0.72,0,1)] [&>[data-panel]]:transition-[flex-grow] [&>[data-panel]]:duration-300'
			)}
		>
			<ResizablePanel
				id="layout-main"
				minSize={rightPanel?.mainMinSize}
			>
				{children}
			</ResizablePanel>
			{rightPanel && (
				<>
					<ResizableHandle
						className={cn(rightPanel.handleClassName, !open && 'hidden')}
						disableDoubleClick
						onPointerDown={handleDragStart}
					/>
					<ResizablePanel
						id="layout-right-panel"
						// Non-collapsible while open so a drag hard-clamps at minSize; flips collapsible on close so collapse() can run (a drag only ever starts from the open state).
						collapsible={!open}
						// Already-open at mount (restored across navigation) renders at full width instantly.
						defaultSize={open ? rightPanel.getOpenSize() : '0px'}
						minSize={rightPanel.minSize}
						maxSize={rightPanel.maxSize}
						groupResizeBehavior={rightPanel.groupResizeBehavior}
						panelRef={panelRef}
						onResize={(size, _id, prevSize) => {
							// No prevSize = the library's mount report (fires at 0 on remount) — not a user resize.
							if (!prevSize) return;
							// onResize fires per animation frame, so persist only user-dragged widths.
							if (isDragging && size.inPixels > 0) rightPanel.onUserResize?.(size);
						}}
					>
						<div className="relative z-20 h-full overflow-hidden">
							{/* Pinned at min width so the open/close animation clips content instead of rewrapping it. */}
							<div
								className="h-full"
								style={
									rightPanel.contentMinWidthPx
										? { minWidth: rightPanel.contentMinWidthPx }
										: undefined
								}
							>
								{hasOpened && rightPanel.node}
							</div>
						</div>
					</ResizablePanel>
				</>
			)}
		</ResizablePanelGroup>
	);
}
